import type { UIMessage, UseChatHelpers } from "@ai-sdk/react";
import { useChat } from "@ai-sdk/react";
import { useIdToken } from "@sans-service-tier/sdk-react";
import { DefaultChatTransport } from "ai";
import { useEffect, useMemo, useRef } from "react";

import { env } from "../util/env";
import { useAppState } from "./useAppState";

export type AiSmeChat = UseChatHelpers<UIMessage>;

export function useAiSmeChat({
  enabled,
  onData,
  onError,
  onFinish,
}: {
  enabled: boolean;
  onData: () => void;
  onError: (error: Error) => void;
  onFinish: () => void;
}) {
  const { course, section, module, slide } = useAppState();
  const { token: accessToken } = useIdToken();

  // store the course context in a ref to be accessed by the transport
  const courseContextRef = useRef({
    currentSection: section?.name,
    currentLO: module?.name,
    currentCourseOutline: course?.sections?.map(section => section.name).join(", "),
    currentSlide: slide?.name,
    currentSlideSummary: undefined,
  });

  // update the course context when the course, section, module, or slide changes
  useEffect(() => {
    courseContextRef.current = {
      currentSection: section?.name,
      currentLO: module?.name,
      currentCourseOutline: course?.sections?.map(section => section.name).join(", "),
      currentSlide: slide?.name,
      currentSlideSummary: undefined,
    };
  }, [course, section, module, slide]);

  const transport = useMemo(() => {
    if (!course?.trainingId || !course?.shortName || !accessToken) return undefined;

    // if we're on a demo course, do nothing and return undefined
    const isDemoCourse = course.courseType === "Demo";
    if (isDemoCourse) return undefined;

    // get the base URL for the AI chat SME service
    const AiChatSmeServiceBaseUrl = env.VITE_AI_SME_CHAT_SERVICE_URL;
    if (!AiChatSmeServiceBaseUrl) {
      console.error("VITE_AI_SME_CHAT_SERVICE_URL environment variable is not set");
      return undefined;
    }
    const currentApiEndpointId = course?.trainingId;
    const AiChatSmeServiceUrl = `${AiChatSmeServiceBaseUrl}/chat/${currentApiEndpointId}/stream`;
    const currentCourseName = course?.shortName;

    try {
      return new DefaultChatTransport({
        api: AiChatSmeServiceUrl,
        headers: {
          "X-Access-Token": accessToken,
        },
        fetch: async (_, options) => {
          // Parse the original body
          const originalBody = JSON.parse(options?.body as string);

          // Add contentId and courseContext to the request
          const modifiedBody = {
            ...originalBody,
            contentId: currentCourseName,
            courseContext: courseContextRef.current,
          };

          // Create new options with the modified body and updated token
          const modifiedOptions = {
            ...options,
            body: JSON.stringify(modifiedBody),
            headers: {
              ...options?.headers,
              "X-Access-Token": accessToken,
            },
          };

          // Use the dynamically constructed URL
          return fetch(AiChatSmeServiceUrl, modifiedOptions);
        },
      });
    } catch (error) {
      console.error("Error creating chat transport:", error);
      return undefined;
    }
  }, [course?.trainingId, course?.shortName, course?.courseType, accessToken, courseContextRef]);

  // Use a stable id that changes when enabled state changes, to force useChat to reinitialize.
  // This prevents a race condition where useChat initializes with undefined transport
  // and falls back to the default /api/chat endpoint.
  const chatId = enabled && transport ? `ai-sme-chat-${course?.trainingId}` : undefined;

  const chat = useChat({
    id: chatId,
    transport: enabled ? transport : undefined,
    onData,
    onFinish,
    onError,
    // We need the experimental_throttle setting, as without it, React will render on every chunk streamed in,
    // potentially causing 100's of re-renders per second, which exceeds the maximum update depth.
    // 50ms (20 FPS) is an okay compromise between performance and the UI continuing to render.
    // https://ai-sdk.dev/docs/troubleshooting/react-maximum-update-depth-exceeded
    experimental_throttle: 50,
  });

  return chat;
}
